import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  DestroyRef,
  Directive,
  ElementRef,
  HostListener,
  inject,
  input,
  OnDestroy,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { TooltipPosition } from '../utils/tooltip-position.type';
import { computeTooltipPosition } from '../utils/compute-tooltip-position';
import { UiTooltipComponent } from './ui-tooltip.component';

@Directive({
  selector: '[libTooltip]',
  standalone: false,
})
export class TooltipDirective implements OnDestroy {
  readonly tooltipMessage = input('');
  readonly tooltipDisabled = input(false);
  readonly tooltipHideDelay = input(100);
  readonly tooltipShowDelay = input(150);
  readonly tooltipPosition = input<TooltipPosition>('top');
  readonly tooltipClass = input<
    string | string[] | Record<string, boolean> | null
  >(null);

  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly appRef = inject(ApplicationRef);
  private readonly doc = inject(DOCUMENT);
  private readonly destroy = inject(DestroyRef);

  private tooltipRef?: ComponentRef<UiTooltipComponent>;
  private showId?: ReturnType<typeof setTimeout>;
  private hideId?: ReturnType<typeof setTimeout>;

  @HostListener('mouseenter')
  @HostListener('focusin')
  onShow(): void {
    if (this.tooltipDisabled() || !this.tooltipMessage()?.trim()) return;
    this.clearHide();
    this.clearShow();
    this.showId = setTimeout(
      () => this.createTooltip(),
      this.tooltipShowDelay()
    );
  }

  @HostListener('mouseleave')
  @HostListener('blur')
  onHide(): void {
    this.clearShow();
    if (!this.tooltipRef) return;
    this.clearHide();
    this.hideId = setTimeout(
      () => this.destroyTooltip(),
      this.tooltipHideDelay()
    );
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  onViewportChange(): void {
    if (this.tooltipRef) this.positionTooltip();
  }

  ngOnDestroy(): void {
    this.clearShow();
    this.clearHide();
    this.destroyTooltip();
  }

  private createTooltip(): void {
    if (
      this.tooltipRef ||
      this.tooltipDisabled() ||
      !this.tooltipMessage()?.trim()
    )
      return;

    this.tooltipRef = createComponent(UiTooltipComponent, {
      environmentInjector: this.appRef.injector,
    });

    const inst = this.tooltipRef.instance;
    inst.messageInput.set(this.tooltipMessage());
    inst.positionInput.set(this.tooltipPosition());
    inst.tooltipClassInput.set(this.tooltipClass());

    this.appRef.attachView(this.tooltipRef.hostView);
    const tooltipEl = this.tooltipRef.location.nativeElement as HTMLElement;
    this.doc.body.appendChild(tooltipEl);
    this.positionTooltip();

    const id = `tooltip-${Math.random().toString(36).slice(2, 10)}`;
    this.el.nativeElement.setAttribute('aria-describedby', id);
    tooltipEl.id = id;
  }

  private destroyTooltip(): void {
    if (!this.tooltipRef) return;
    this.appRef.detachView(this.tooltipRef.hostView);
    this.tooltipRef.destroy();
    this.tooltipRef = undefined;
    this.el.nativeElement.removeAttribute('aria-describedby');
  }

  private positionTooltip(): void {
    if (!this.tooltipRef) return;
    const tooltip = this.tooltipRef.location.nativeElement as HTMLElement;
    tooltip.style.visibility = 'hidden';
    tooltip.style.top = '0px';
    tooltip.style.left = '0px';
    requestAnimationFrame(() => {
      const pos = computeTooltipPosition(
        this.el.nativeElement.getBoundingClientRect(),
        tooltip.getBoundingClientRect(),
        this.tooltipPosition()
      );
      tooltip.style.left = `${pos.left}px`;
      tooltip.style.top = `${pos.top}px`;
      tooltip.style.visibility = 'visible';
    });
  }

  private clearShow(): void {
    clearTimeout(this.showId);
    this.showId = undefined;
  }

  private clearHide(): void {
    clearTimeout(this.hideId);
    this.hideId = undefined;
  }
}
